import { useState } from "react"
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { MessageCircle, Plus, Search, Trash2 } from "lucide-react"
import { toast } from "sonner"
import type { SavedReply } from "@vienna/contracts"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Field, Heading, MiniAvatar } from "@/components/workspace/primitives"
import { api, ApiError } from "@/lib/api"
import { savedRepliesQuery } from "@/lib/queries"
import { useCan, useWorkspaceContext } from "@/lib/session"

export const Route = createFileRoute("/w/$workspace/settings/saved-replies")({
  loader: ({ context, params }) => context.queryClient.ensureQueryData(savedRepliesQuery(params.workspace)),
  component: SavedReplies,
})

type Draft = { id: string | null; name: string; shortcut: string; text: string }

function personalize(text: string, firstName: string | null) {
  return text.replaceAll(/\{\{\s*first_name(?:\s*\|\s*([^}]*))?\s*\}\}/g, (_m, fallback: string | undefined) => firstName?.trim() || (fallback?.trim() ?? "there"))
}

/** Screen 43. */
function SavedReplies() {
  const { workspace } = useWorkspaceContext()
  const can = useCan()
  const manage = can("manage_support")
  const queryClient = useQueryClient()
  const replies = useSuspenseQuery(savedRepliesQuery(workspace.slug)).data
  const [search, setSearch] = useState("")
  const [draft, setDraft] = useState<Draft | null>(replies[0] ? { id: replies[0].id, name: replies[0].name, shortcut: replies[0].shortcut, text: replies[0].text } : null)
  const [missing, setMissing] = useState(false)
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["workspace", workspace.slug, "saved-replies"] })
  const save = useMutation({
    mutationFn: (d: Draft) => api<SavedReply>(d.id ? `/w/${workspace.slug}/saved-replies/${d.id}` : `/w/${workspace.slug}/saved-replies`, { method: d.id ? "PUT" : "POST", body: JSON.stringify({ name: d.name.trim(), shortcut: d.shortcut.trim(), text: d.text }) }),
    onSuccess: async (saved) => {
      await refresh()
      setDraft({ id: saved.id, name: saved.name, shortcut: saved.shortcut, text: saved.text })
      toast.success("Reply saved")
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.problem.title : "Could not save the reply"),
  })
  const remove = useMutation({
    mutationFn: (id: string) => api<void>(`/w/${workspace.slug}/saved-replies/${id}`, { method: "DELETE" }),
    onSuccess: async () => {
      await refresh()
      setDraft(null)
      toast("Reply deleted")
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.problem.title : "Could not delete the reply"),
  })
  const visible = replies.filter((r) => `${r.name} ${r.shortcut}`.toLowerCase().includes(search.toLowerCase()))
  return (
    <>
      <Heading
        title="Saved replies"
        description="Thoughtful answers, without typing them twice."
        action={
          manage ? (
            <Button onClick={() => setDraft({ id: null, name: "Untitled reply", shortcut: "/new", text: "Hi {{first_name}}, " })}>
              <Plus />
              New reply
            </Button>
          ) : undefined
        }
      />
      <div className="ws-reply-layout">
        <aside>
          <div className="ws-search">
            <Search size={15} />
            <Input aria-label="Search saved replies" placeholder="Search replies…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div className="ws-reply-items">
            {visible.map((r) => (
              <button onClick={() => setDraft({ id: r.id, name: r.name, shortcut: r.shortcut, text: r.text })} key={r.id} className={draft?.id === r.id ? "selected" : ""}>
                <MessageCircle size={16} />
                <div>
                  <strong>{r.name}</strong>
                  <small>{r.shortcut}</small>
                </div>
              </button>
            ))}
            {visible.length === 0 && <p className="ws-hint p-3">{replies.length === 0 ? "No saved replies yet." : "No replies match."}</p>}
          </div>
        </aside>
        {draft ? (
          <div className="ws-reply-editor">
            <div className="ws-form-grid">
              <Field label="Name">{(id) => <Input id={id} value={draft.name} disabled={!manage} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />}</Field>
              <Field label="Shortcut" hint="Starts with / and uses lowercase letters.">
                {(id) => <Input id={id} value={draft.shortcut} disabled={!manage} onChange={(e) => setDraft({ ...draft, shortcut: e.target.value })} />}
              </Field>
            </div>
            <Field label="Message">{(id) => <Textarea id={id} className="min-h-36" value={draft.text} disabled={!manage} onChange={(e) => setDraft({ ...draft, text: e.target.value })} />}</Field>
            {manage && (
              <Button size="sm" variant="outline" onClick={() => setDraft({ ...draft, text: `${draft.text}{{first_name}}` })}>
                <Plus />
                Insert first name
              </Button>
            )}
            <div className="ws-reply-preview">
              <div className="ws-flex-between">
                <span>PERSONALIZATION PREVIEW</span>
                <label className="ws-checkbox-text">
                  <Checkbox checked={missing} onCheckedChange={(v) => setMissing(v === true)} />
                  Missing first name
                </label>
              </div>
              <div className="ws-person">
                <MiniAvatar name={missing ? "Unknown Contact" : "Maya Johnson"} />
                <strong>{missing ? "Contact without a first name" : "Maya Johnson"}</strong>
              </div>
              <p>{personalize(draft.text, missing ? null : "Maya")}</p>
            </div>
            <p className="ws-hint">Missing first names use “there” so the greeting still reads naturally.</p>
            {manage && (
              <div className="ws-save">
                <span>Changes apply to {workspace.name}.</span>
                <span className="flex gap-2">
                  {draft.id && (
                    <Button variant="ghost" onClick={() => remove.mutate(draft.id!)} disabled={remove.isPending}>
                      <Trash2 />
                      Delete
                    </Button>
                  )}
                  <Button onClick={() => save.mutate(draft)} disabled={save.isPending || !draft.name.trim() || !draft.text.trim()}>
                    Save reply
                  </Button>
                </span>
              </div>
            )}
          </div>
        ) : (
          <div className="ws-reply-editor">
            <p className="ws-empty">{manage ? "Create a reply or pick one from the list." : "Pick a reply from the list to preview it."}</p>
          </div>
        )}
      </div>
    </>
  )
}
